import { useState } from 'react'

import { ПЛАНЕТЫ, leverTorque, ru, springScaleKg } from '../../lib/physics'
import { Choice, Predict, Slider, useFrames } from './common'

/**
 * Весы на Земле и на Луне (урок 35): рычажные сравнивают силы тяжести груза и гирь —
 * равновесие на любой планете; пружинные измеряют силу и, градуированные на Земле,
 * на Луне показывают примерно в 6 раз меньше.
 */

const ГИРИ = [5, 1, 0.5, 0.1] as const
const МАКС_УГОЛ = 14 // градусов

export default function ScalesLab() {
  const [планета, setПланета] = useState(0)
  const [груз, setГруз] = useState(6)
  const [гири, setГири] = useState<number[]>([])
  const [наклон, setНаклон] = useState({ a: 0, w: 0 })

  const g = ПЛАНЕТЫ[планета].g
  const массаГирь = Math.round(гири.reduce((a, b) => a + b, 0) * 10) / 10
  const момент = leverTorque(груз, массаГирь, g)
  const пружинные = springScaleKg(груз, g)

  useFrames((dt) => {
    setНаклон((s) => {
      // коромысло качается к своему положению; при меньшем g — медленнее
      const цель = Math.max(-МАКС_УГОЛ, Math.min(МАКС_УГОЛ, момент * 3))
      const k = g * 1.6
      const acc = -k * (s.a - цель) - 2.5 * s.w
      const w = s.w + acc * dt
      return { a: s.a + w * dt, w }
    })
  })

  const уголРад = (наклон.a * Math.PI) / 180
  const плечо = 70
  const лево = { x: 90 - плечо * Math.cos(уголРад), y: 90 + плечо * Math.sin(уголРад) }
  const право = { x: 90 + плечо * Math.cos(уголРад), y: 90 - плечо * Math.sin(уголРад) }
  const стрелка = (пружинные / 12) * 300 - 150 // градусы на циферблате 0…12 кг

  return (
    <div className="card">
      <div className="lab-scales">
        <svg className="lab-svg" viewBox="0 0 180 170" role="img" aria-label="Рычажные весы">
          <polygon points="90,90 76,150 104,150" className="fill-muted" />
          <line x1={лево.x} y1={лево.y} x2={право.x} y2={право.y} className="ink" strokeWidth={4} strokeLinecap="round" />
          <line x1={лево.x} y1={лево.y} x2={лево.x} y2={лево.y + 30} className="ink" />
          <line x1={право.x} y1={право.y} x2={право.x} y2={право.y + 30} className="ink" />
          <line x1={лево.x - 22} y1={лево.y + 30} x2={лево.x + 22} y2={лево.y + 30} className="ink" strokeWidth={3} />
          <line x1={право.x - 22} y1={право.y + 30} x2={право.x + 22} y2={право.y + 30} className="ink" strokeWidth={3} />
          <circle cx={лево.x} cy={лево.y + 19} r={11} className="fill-melon ink" />
          <text x={лево.x} y={лево.y + 6} textAnchor="middle" className="lab-tick">{ru(груз, 1)} кг</text>
          {массаГирь > 0 && (
            <>
              <rect x={право.x - 12} y={право.y + 14} width={24} height={16} rx={2} className="fill-body ink" />
              <text x={право.x} y={право.y + 9} textAnchor="middle" className="lab-tick">{ru(массаГирь, 1)} кг</text>
            </>
          )}
          <text x={90} y={166} textAnchor="middle" className="lab-label">рычажные</text>
        </svg>

        <svg className="lab-svg" viewBox="0 0 180 170" role="img" aria-label="Пружинные весы">
          <rect x={30} y={100} width={120} height={40} rx={6} className="fill-cabin ink" strokeWidth={2} />
          <circle cx={90} cy={60} r={46} className="fill-cabin ink" strokeWidth={2} />
          {Array.from({ length: 13 }, (_, i) => {
            const a = ((i / 12) * 300 - 150 - 90) * (Math.PI / 180)
            return (
              <g key={i}>
                <line x1={90 + 38 * Math.cos(a)} y1={60 + 38 * Math.sin(a)} x2={90 + 44 * Math.cos(a)} y2={60 + 44 * Math.sin(a)} className="ink" />
                {i % 2 === 0 && <text x={90 + 30 * Math.cos(a)} y={63 + 30 * Math.sin(a)} textAnchor="middle" className="lab-tick">{i}</text>}
              </g>
            )
          })}
          <line
            x1={90} y1={60}
            x2={90 + 36 * Math.cos(((стрелка - 90) * Math.PI) / 180)} y2={60 + 36 * Math.sin(((стрелка - 90) * Math.PI) / 180)}
            className="ink-heavy" strokeWidth={2.5}
          />
          <circle cx={90} cy={60} r={3} className="fill-heavy" />
          <text x={90} y={124} textAnchor="middle" className="lab-label">{ru(пружинные, 2)} кг</text>
          <text x={90} y={166} textAnchor="middle" className="lab-label">пружинные</text>
        </svg>
      </div>

      <div className="lab-readout">
        <span>g = {ru(g, 1)} Н/кг</span>
        <span>рычажные: {Math.abs(груз - массаГирь) < 0.05 ? 'равновесие' : груз > массаГирь ? 'перевешивает груз' : 'перевешивают гири'}</span>
        <span>пружинные показывают {ru(пружинные, 2)} кг</span>
      </div>

      <Predict
        question={`На Земле пружинные весы показали ${ru(груз, 1)} кг. Что они покажут на Луне?`}
        options={[`${ru(груз, 1)} кг`, `около ${ru(springScaleKg(груз, 1.6), 1)} кг`, '0 кг', `больше ${ru(груз, 1)} кг`]}
        right={1}
        revealed={ПЛАНЕТЫ[планета].имя === 'Луна'}
        resetKey={String(груз)}
        explain="Пружинные весы измеряют силу, а на Луне сила тяжести примерно в 6 раз меньше. Шкалу в килограммах размечали на Земле — поэтому они «врут»."
      />

      <div className="lab-sub">Где взвешиваем</div>
      <Choice options={ПЛАНЕТЫ.map((p, i) => ({ value: i, label: p.имя }))} value={планета} onChange={setПланета} />
      <Slider label="Масса арбуза" value={груз} min={1} max={10} step={0.5} unit="кг" shown={ru(груз, 1)} onChange={setГруз} />
      <div className="lab-sub">Гири на правую чашку</div>
      <div className="row-wrap">
        {ГИРИ.map((m) => (
          <button key={m} className="btn" onClick={() => setГири((x) => [...x, m])}>+ {ru(m, m < 1 ? 1 : 0)} кг</button>
        ))}
        <button className="btn" onClick={() => setГири([])}>снять гири</button>
      </div>
      <p className="muted small">
        Уравновесь арбуз гирями на Земле, потом перенеси весы на Луну: равновесие останется —
        сила тяжести уменьшилась одинаково для арбуза и для гирь.
      </p>
    </div>
  )
}
